import { type DisplayDocument, Document } from '@startuptools/business-ui/document/Document';
import { type GqlCompany, type GqlShareIssueRegistration } from '../../../../graphql/base-types.graphql';
import { useState } from 'react';
import {
  type GqlValidatorStatus,
  useCompanySuspenseQuery,
  useDocGenSiRegistrationFormMutation,
  useShareIssueSuspenseQuery,
  useSirErrorsLazyQuery,
  useMeetingHasQesRegFormsSuspenseQuery,
} from '../../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { hasValidatorErrors } from '../../utils';
import { apiClient } from '../../../../common/http';
import { isNil, omit } from 'lodash-es';
import { InnerStripe } from '../../../../components/stripe/Stripe';
import { SiDocumentsShowDocument } from '../../../share-issues/component/DocumentsShow.graphql';
import { GqlDocSystemTagKind, GqlDocumentSignatureMethod } from '../../../../graphql/base-types.graphql';
import { Divider } from '@startuptools/ui/Divider';
import { DocumentSignatures } from '../../../../components/document-signatures/DocumentSignatures';
import { useCompanySubscriptionSuspenseQuery } from '../../../../components/side-nav/CompanySubscription.graphql';
import { ShareIssueDocument } from '../../../share-issues/ShareIssue.graphql';
import { useNavigate } from '../../../../components/Navigate';

const getDefaultFilename = (sir: Pick<GqlShareIssueRegistration, 'description'>, company: Pick<GqlCompany, 'name'>) => {
  return `Registreringsformulär nyemission - ${company.name} - ${sir?.description.substring(0, 40)}`;
};

export const ShareIssueRegistrationForm = ({
  shareIssueId,
  shareIssueRegistration,
}: {
  shareIssueId: string;
  shareIssueRegistration: Pick<GqlShareIssueRegistration, 'id' | 'description' | 'signatoryId'> & {
    latestRegistrationForm?: DisplayDocument | null;
  };
}) => {
  const companyId = useCompanyId();
  const { navigate } = useNavigate();

  const [showStripeDialog, setShowStripeDialog] = useState(false);

  const [fetchSirErrors] = useSirErrorsLazyQuery({
    variables: { companyId, id: shareIssueRegistration.id },
    fetchPolicy: 'no-cache',
  });

  const {
    data: { shareIssue },
  } = useShareIssueSuspenseQuery({ variables: { companyId, id: shareIssueId } });
  const {
    data: { company },
  } = useCompanySuspenseQuery({ variables: { id: companyId } });
  const { data: { companySubscription } = { companySubscription: null } } = useCompanySubscriptionSuspenseQuery({
    variables: { companyId },
  });
  const { data: { meetingHasQesRegForms } = { meetingHasQesRegForms: false } } = useMeetingHasQesRegFormsSuspenseQuery({
    variables: {
      companyId,
      input: { id: shareIssue.meetingConcern?.meetingId ?? '', skipId: shareIssueRegistration.id },
    },
    skip: !shareIssue.meetingConcern?.meetingId,
    fetchPolicy: 'no-cache',
  });

  // eslint-disable-next-line sut/loading-mutation
  const [doGenerate] = useDocGenSiRegistrationFormMutation({
    refetchQueries: [ShareIssueDocument, SiDocumentsShowDocument],
    awaitRefetchQueries: true,
  });
  const defaultFilename = getDefaultFilename(shareIssueRegistration, company);

  const companyCanUseProFeatures = companySubscription?.canUseProFeatures ?? false;

  return (
    <>
      <Document
        allowDigitalSignature={companyCanUseProFeatures}
        allowGenerationWithWarnings={false}
        canUseProFeatures={companyCanUseProFeatures}
        document={shareIssueRegistration?.latestRegistrationForm}
        documentSystemTagKind={GqlDocSystemTagKind.ShareIssueRegistrationForm}
        hasDigitalRegistrationForms={meetingHasQesRegForms}
        defaultFileName={defaultFilename}
        generate={async signatureMethod => {
          if (isNil(shareIssue.paidForAt)) {
            setShowStripeDialog(true);
            return;
          }
          return doGenerate({
            variables: {
              companyId,
              sirId: shareIssueRegistration.id,
              signatureMethod: signatureMethod ?? GqlDocumentSignatureMethod.Analogue,
            },
          });
        }}
        preview={() => apiClient.previewShareIssueRegistrationForm(companyId, shareIssueRegistration.id)}
        validate={async () => {
          const { data: sirErrorData } = await fetchSirErrors();
          if (!sirErrorData) {
            return { isValid: false, errors: undefined };
          }
          const sirErrors = omit(sirErrorData.shareIssueRegistrationErrors, 'todos');

          if (hasValidatorErrors(sirErrors)) {
            const withActions = sirErrors as Record<string, GqlValidatorStatus & { action?: React.ReactNode }>;
            return { isValid: false, errors: Object.values(withActions) };
          }
          return { isValid: true, errors: undefined };
        }}
        signaturesSlot={
          shareIssueRegistration.latestRegistrationForm?.signatureMethod === GqlDocumentSignatureMethod.Digital &&
          companyCanUseProFeatures && (
            <>
              <Divider />
              <DocumentSignatures
                documentId={shareIssueRegistration.latestRegistrationForm.id}
                scriveStatus={shareIssueRegistration.latestRegistrationForm.scriveStatus}
                zignedAgreementStatus={shareIssueRegistration.latestRegistrationForm.zignedAgreementStatus}
                signAllowedAction={shareIssueRegistration.latestRegistrationForm.signAllowedAction}
                isQualifiedEsign
              />
            </>
          )
        }
      />

      {showStripeDialog && shareIssue.meetingConcern?.meetingId && (
        <InnerStripe
          companyId={shareIssue.companyId}
          meetingId={shareIssue.meetingConcern.meetingId}
          onCancel={() => {
            setShowStripeDialog(false);
          }}
          onPaid={() => {
            setShowStripeDialog(false);
            return doGenerate({
              variables: {
                companyId,
                sirId: shareIssueRegistration.id,
                signatureMethod:
                  shareIssueRegistration.latestRegistrationForm?.signatureMethod ?? GqlDocumentSignatureMethod.Analogue,
              },
            });
          }}
          onBillingRedirect={() => navigate('/settings/billing')}
        />
      )}
    </>
  );
};
